import { type ComponentPropsWithoutRef, type ReactNode, useState } from 'react';

import { ContextMenu, renderContextMenuItems } from '@air/primitive-context-menu';
import { cn } from '@air/utils-tailwind';

import { ContentCollectionGalleryAssetButton } from '~/components/ContentCollection/ContentCollectionGalleryAssetButton';
import { type GetSelectionMenuOptionsType } from '~/components/Gallery/types';
import { type GetItemMenuOptions } from '~/constants/MenuOptions';
import { type UseSelectableCardReturnType, useSelectableCard } from '~/hooks/useSelectableCard';
import { useSelectionAwareContextMenuOptions } from '~/hooks/useSelectionAwareContextMenuOptions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';

export type ContentCollectionGalleryAssetCardProps<Item extends SelectableGalleryItem> = Pick<
  ComponentPropsWithoutRef<'div'>,
  'className'
> & {
  children: (
    props: UseSelectableCardReturnType & { setIsMenuOpen: (isOpen: boolean) => void; isMenuOpen: boolean },
  ) => ReactNode;
  getItemMenuOptions: GetItemMenuOptions;
  getSelectionMenuOptions: GetSelectionMenuOptionsType;
  isSelectable: boolean;
  isInvalid?: boolean;
  item: Item;
  onClick?: () => void;
};

export const ContentCollectionGalleryAssetCard = <Item extends SelectableGalleryItem>({
  className,
  children,
  getItemMenuOptions,
  getSelectionMenuOptions,
  isSelectable,
  isInvalid,
  item,
  onClick,
}: ContentCollectionGalleryAssetCardProps<Item>) => {
  const {
    onBlur,
    onFocus,
    handleHover,
    isHovering,
    handleItemClick,
    handleItemDoubleClick,
    handleUnhover,
    confirmItemSelection,
    preSelectItem,
    itemRef,
    isSelected,
    isHighlighted,
  } = useSelectableCard({
    item,
    onClick,
    isSelectable,
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const contextMenuOptions = getItemMenuOptions({ trackLocation: 'gallery-item-context-menu' });
  const { contentClassName, options } = useSelectionAwareContextMenuOptions({
    contextMenuOptions,
    id: item.id,
    selectionMenuOptions: getSelectionMenuOptions('gallery-multi-select-context-menu'),
  });

  return (
    <ContextMenu
      className={contentClassName}
      trigger={
        // biome-ignore lint/a11y/noStaticElementInteractions: hover and focus tracking only; ContentCollectionGalleryAssetButton is the control
        <div
          data-testid="CONTENT_COLLECTION_GALLERY_ASSET_CARD"
          data-title={item.item.title}
          data-is-selected={isSelected}
          data-is-invalid={isInvalid}
          className={cn(
            'flex size-full flex-col gap-2 rounded-8 ads:bg-base bg-gray-1 p-2 shadow-kanban',
            (isSelected || isHighlighted) && 'ads:ring-primary ring-2 ring-blue-9',
            className,
          )}
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseEnter={handleHover}
          onMouseLeave={handleUnhover}
          ref={itemRef}
        >
          <ContentCollectionGalleryAssetButton
            confirmItemSelection={confirmItemSelection}
            isSelectable={isSelectable}
            onClick={onClick}
            preSelectItem={preSelectItem}
          />
          {children({
            onBlur,
            onFocus,
            handleHover,
            isHovering: isHovering || isMenuOpen,
            handleItemClick,
            handleItemDoubleClick,
            handleUnhover,
            confirmItemSelection,
            preSelectItem,
            itemRef,
            isSelected,
            isHighlighted,
            setIsMenuOpen,
            isMenuOpen,
          })}
        </div>
      }
    >
      {renderContextMenuItems({ options })}
    </ContextMenu>
  );
};
